import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BetaSplash from "@/pages/BetaSplash";
import { LogoLoader } from "@/components/LogoLoader";

// ============================================================================
// PRE-LAUNCH: the site is PRIVATE. Every route is gated — an anonymous visitor
// with no unlocked session sees the CLASSIFIED beta splash EVERYWHERE, "/" included.
// Entering the beta code happens ON the splash for whatever route was requested, so
// no marketing/login path needs to be public to unlock or log in.
// BETA_SPLASH_ENABLED (server `/beta/status` .enabled) remains the master switch.
// ============================================================================

// MARKETING / funnel allow-list — INACTIVE (pre-launch). The whole site, "/" included,
// sits behind the CLASSIFIED splash for anonymous visitors. The route table is kept here,
// dormant behind the flag, so launch is a one-line flip to `true` — no rebuild. Only the
// FUNCTIONAL exemptions below stay open while gated. BETA_SPLASH_ENABLED (server
// `/beta/status`.enabled) is the master switch above all of this.
const MARKETING_ALLOWLIST_ENABLED = false;
const GATE_EXEMPT_MARKETING_PATHS = [
  // marketing + legal front door
  "/", "/pricing", "/faq", "/about", "/ambassadors",
  "/terms", "/privacy", "/legal", "/prohibited-items",
  // signup + login funnel: /join is the entry point but it DELEGATES to /register
  // and /forgot-password, and /login redirects to /join — so all four must be public
  // or the funnel dead-ends at the splash. (/reset-password + /verify-email + the
  // OAuth callback are ALWAYS-open functional exemptions below, not here.)
  "/join", "/login", "/register", "/forgot-password",
];

// FUNCTIONAL exemptions — ALWAYS active, never gate-able regardless of the flag:
// the OAuth callback returns via a FULL page load and must not be swallowed, and
// emailed single-use-token links (password reset / email + contact verification /
// security disavow) must open exactly what they point at, never the splash. A stranger
// clicking one of these has no beta code, so these can't sit behind the gate.
// /settings/payouts is here too: a seller RETURNS to it from Stripe Connect onboarding
// (often a fresh tab with no beta-unlock), and must NEVER hit the CLASSIFIED splash after
// handing Stripe their SSN + bank details. The page self-guards via ProtectedRoute.
const GATE_EXEMPT_PATHS = ["/auth/google", "/verify-email", "/reset-password", "/verify", "/settings/payouts"];
// Prefixes: "/security" covers /security/disavow; "/contact-change/" covers the verify link.
const GATE_EXEMPT_PREFIXES = ["/security", "/contact-change/"];

const BETA_UNLOCK_KEY = "os_beta_unlocked";

export default function BetaGate({ children }) {
  // Persist the unlock across reloads + navigation. Initialise straight from storage so a
  // returning (already-unlocked) tester never even flashes the splash on refresh.
  const [state, setState] = useState(() =>
    (typeof window !== "undefined" && localStorage.getItem(BETA_UNLOCK_KEY) === "1") ? "open" : "checking"
  ); // checking | locked | open
  const [status, setStatus] = useState({ full: false });
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // Anonymous gate check. A persisted unlock (localStorage) wins immediately, so a tester
  // who already entered the code is NOT re-gated on the next route or a page refresh.
  // FAIL CLOSED: if we can't get an explicit `enabled: false` (network/5xx/CORS) we show
  // the splash rather than leak the site. One retry absorbs blips.
  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(BETA_UNLOCK_KEY) === "1") {
      setState("open");
      return;
    }
    let cancelled = false;
    const check = async (attempt = 0) => {
      try {
        const { data } = await api.get("/beta/status");
        if (cancelled) return;
        setStatus(data);
        setState(data.enabled ? "locked" : "open");
      } catch {
        if (attempt < 1) return check(attempt + 1);
        if (cancelled) return;
        setState("locked"); // fail closed — never leak the site to anonymous visitors
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  // Unlock: write the persistent flag BEFORE flipping state — the write-before-redirect fix
  // for the old "re-gates on reload" bug. We do NOT navigate: the splash renders in place at
  // whatever route was requested, so opening the gate simply renders that route's page —
  // requested /feed lands on /feed; requested / (or no destination) lands on the landing page.
  const handleUnlock = () => {
    try { localStorage.setItem(BETA_UNLOCK_KEY, "1"); } catch (e) {}
    setState("open");
  };

  // 1) ALWAYS-open routes: functional emailed-link / OAuth targets, plus the marketing
  //    allow-list ONLY when it's switched on for launch. Pre-launch, marketing stays gated.
  const path = location.pathname;
  const marketingOpen = MARKETING_ALLOWLIST_ENABLED && GATE_EXEMPT_MARKETING_PATHS.includes(path);
  if (marketingOpen
      || GATE_EXEMPT_PATHS.includes(path)
      || GATE_EXEMPT_PREFIXES.some((p) => path.startsWith(p))) return children;

  // 2) Wait for auth to resolve so we never flash the splash at a logged-in user.
  //    Anonymous visitors (no token) resolve to `false` synchronously → no real wait.
  if (authLoading || user === null) return <LogoLoader />;

  // 3) Authenticated users bypass the gate — they've already proven access (this is
  //    also what lets a just-completed login land on /feed instead of being re-gated).
  if (user) return children;

  // 4) Anonymous visitors: enforce the confidential gate (fail-closed).
  if (state === "checking") return <LogoLoader />;
  if (state === "locked") return <BetaSplash onUnlock={handleUnlock} full={status.full} />;
  return children;
}
